import { Link as PdfLink, StyleSheet, Text, View } from "@react-pdf/renderer";
import { ReactNode } from "react";

const pdfStyles = StyleSheet.create({
  container: {
    fontSize: 11,
    color: "#30313d",
    lineHeight: 1.4,
  },
  h1: {
    fontSize: 18,
    fontFamily: "Helvetica-Bold",
    marginTop: 14,
    marginBottom: 8,
  },
  h2: {
    fontSize: 14,
    fontFamily: "Helvetica-Bold",
    marginTop: 12,
    marginBottom: 6,
  },
  h3: {
    fontSize: 12,
    fontFamily: "Helvetica-Bold",
    marginTop: 10,
    marginBottom: 5,
  },
  paragraph: {
    fontSize: 11,
    marginBottom: 6,
    lineHeight: 1.4,
  },
  list: {
    marginBottom: 0,
  },
  listItem: {
    fontSize: 11,
    marginBottom: 3,
    flexDirection: "row",
  },
  bullet: {
    width: 12,
  },
  number: {
    width: 18,
  },
  itemBody: {
    flex: 1,
  },
  bold: {
    fontFamily: "Helvetica-Bold",
  },
  italic: {
    fontFamily: "Helvetica-Oblique",
  },
  link: {
    color: "#4f46e5",
    textDecoration: "underline",
  },
});

// Dense legal-text layout for documents that must fit a page budget (e.g. the
// Terms Acceptance PDF sent to Stripe as dispute evidence, capped at 17 pages).
const compactPdfStyles = StyleSheet.create({
  container: {
    fontSize: 10,
    color: "#30313d",
    lineHeight: 1.3,
  },
  h1: {
    fontSize: 14,
    fontFamily: "Helvetica-Bold",
    marginTop: 8,
    marginBottom: 4,
  },
  h2: {
    fontSize: 12,
    fontFamily: "Helvetica-Bold",
    marginTop: 6,
    marginBottom: 3,
  },
  h3: {
    fontSize: 11,
    fontFamily: "Helvetica-Bold",
    marginTop: 5,
    marginBottom: 2,
  },
  paragraph: {
    fontSize: 10,
    marginBottom: 3,
    lineHeight: 1.3,
  },
  list: {
    marginBottom: 2,
  },
  listItem: {
    fontSize: 10,
    marginBottom: 1.5,
    flexDirection: "row",
  },
  bullet: {
    width: 9,
  },
  number: {
    width: 14,
  },
  itemBody: {
    flex: 1,
  },
});

type BlockStyles = typeof compactPdfStyles;

const HEADING_RE = /^(#{1,6})\s+(.*)$/;
const UL_RE = /^\s*[-*]\s+/;
const OL_RE = /^\s*\d+\.\s+/;
const ESCAPE_RE = /\\(\\|n|r|t|"|'|\[|\]|\(|\))/g;
const SAFE_URL_RE = /^(https?:|mailto:|tel:|\/|#)/i;

function findMatchingBracket(text: string, openIndex: number): number {
  let depth = 1;
  let j = openIndex + 1;
  while (j < text.length) {
    const c = text.charAt(j);
    if (c === "\\" && j + 1 < text.length) {
      j += 2;
    } else {
      if (c === "[") {
        depth += 1;
      } else if (c === "]") {
        depth -= 1;
        if (depth === 0) return j;
      }
      j += 1;
    }
  }
  return -1;
}

function unescape(source: string): string {
  return source.replace(ESCAPE_RE, (_, ch) => {
    if (ch === "n") return "\n";
    if (ch === "r") return "\r";
    if (ch === "t") return "\t";
    return ch;
  });
}

function safeHref(url: string): string {
  return SAFE_URL_RE.test(url) ? url : "#";
}

function parseInline(text: string, keyPrefix: string): ReactNode[] {
  const nodes: ReactNode[] = [];
  let buffer = "";
  let keyIdx = 0;
  let i = 0;

  const flushBuffer = () => {
    if (buffer) {
      nodes.push(buffer);
      buffer = "";
    }
  };

  while (i < text.length) {
    const ch = text.charAt(i);
    const next = text.charAt(i + 1);
    let handled = false;

    if (ch === "*" && next === "*") {
      const end = text.indexOf("**", i + 2);
      if (end !== -1) {
        flushBuffer();
        const inner = text.slice(i + 2, end);
        nodes.push(
          <Text key={`${keyPrefix}-b${keyIdx}`} style={pdfStyles.bold}>
            {parseInline(inner, `${keyPrefix}-b${keyIdx}`)}
          </Text>
        );
        keyIdx += 1;
        i = end + 2;
        handled = true;
      }
    }

    if (!handled && (ch === "*" || ch === "_")) {
      const end = text.indexOf(ch, i + 1);
      if (end !== -1 && end !== i + 1) {
        flushBuffer();
        const inner = text.slice(i + 1, end);
        nodes.push(
          <Text key={`${keyPrefix}-i${keyIdx}`} style={pdfStyles.italic}>
            {parseInline(inner, `${keyPrefix}-i${keyIdx}`)}
          </Text>
        );
        keyIdx += 1;
        i = end + 1;
        handled = true;
      }
    }

    if (!handled && ch === "[") {
      const closeBracket = findMatchingBracket(text, i);
      if (closeBracket !== -1 && text.charAt(closeBracket + 1) === "(") {
        const closeParen = text.indexOf(")", closeBracket + 2);
        if (closeParen !== -1) {
          const label = text.slice(i + 1, closeBracket);
          const url = text.slice(closeBracket + 2, closeParen);
          flushBuffer();
          nodes.push(
            <PdfLink
              key={`${keyPrefix}-a${keyIdx}`}
              src={safeHref(url)}
              style={pdfStyles.link}
            >
              {label}
            </PdfLink>
          );
          keyIdx += 1;
          i = closeParen + 1;
          handled = true;
        }
      }
    }

    if (!handled && ch === "\n") {
      flushBuffer();
      nodes.push("\n");
      i += 1;
      handled = true;
    }

    if (!handled) {
      buffer += ch;
      i += 1;
    }
  }

  flushBuffer();
  return nodes;
}

function parseBlocks(source: string, blockStyles: BlockStyles): ReactNode[] {
  const normalized = unescape(source).replace(/\r\n/g, "\n");
  const lines = normalized.split("\n");
  const blocks: ReactNode[] = [];
  let i = 0;
  let key = 0;

  while (i < lines.length) {
    const line = lines[i];
    const heading = line.trim() ? HEADING_RE.exec(line) : null;

    if (!line.trim()) {
      i += 1;
    } else if (heading) {
      const level = Math.min(heading[1].length, 6);
      const headingKey = `h-${key}`;
      key += 1;
      const inner = parseInline(heading[2], headingKey);
      let style = blockStyles.h3;
      if (level === 1) style = blockStyles.h1;
      else if (level === 2) style = blockStyles.h2;
      blocks.push(
        <Text key={headingKey} style={style}>
          {inner}
        </Text>
      );
      i += 1;
    } else if (UL_RE.test(line)) {
      const items: ReactNode[] = [];
      while (i < lines.length && UL_RE.test(lines[i])) {
        const itemText = lines[i].replace(UL_RE, "");
        items.push(
          <View key={`li-${i}`} style={blockStyles.listItem}>
            <Text style={blockStyles.bullet}>•</Text>
            <Text style={blockStyles.itemBody}>
              {parseInline(itemText, `li-${i}`)}
            </Text>
          </View>
        );
        i += 1;
      }
      blocks.push(
        <View key={`ul-${key}`} style={blockStyles.list}>
          {items}
        </View>
      );
      key += 1;
    } else if (OL_RE.test(line)) {
      const items: ReactNode[] = [];
      let n = 1;
      while (i < lines.length && OL_RE.test(lines[i])) {
        const itemText = lines[i].replace(OL_RE, "");
        const num = n;
        items.push(
          <View key={`li-${i}`} style={blockStyles.listItem}>
            <Text style={blockStyles.number}>{`${num}.`}</Text>
            <Text style={blockStyles.itemBody}>
              {parseInline(itemText, `li-${i}`)}
            </Text>
          </View>
        );
        n += 1;
        i += 1;
      }
      blocks.push(
        <View key={`ol-${key}`} style={blockStyles.list}>
          {items}
        </View>
      );
      key += 1;
    } else {
      const paragraphLines: string[] = [];
      while (
        i < lines.length &&
        lines[i].trim() &&
        !HEADING_RE.test(lines[i]) &&
        !UL_RE.test(lines[i]) &&
        !OL_RE.test(lines[i])
      ) {
        paragraphLines.push(lines[i]);
        i += 1;
      }
      const paragraphKey = `p-${key}`;
      key += 1;
      blocks.push(
        <Text key={paragraphKey} style={blockStyles.paragraph}>
          {parseInline(paragraphLines.join("\n"), paragraphKey)}
        </Text>
      );
    }
  }

  return blocks;
}

interface IMarkdownPdfProps {
  children: string;
  compact?: boolean;
}

export function MarkdownPdf({ children, compact = false }: IMarkdownPdfProps) {
  const blockStyles = compact ? compactPdfStyles : pdfStyles;
  return (
    <View style={blockStyles.container}>
      {parseBlocks(children, blockStyles)}
    </View>
  );
}
